import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3, Bell, Briefcase, CheckCheck, FilePlus2, Globe, LayoutDashboard,
  LogOut, Menu, ScrollText, Users as UsersIcon, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import api, { apiError } from "../lib/api";
import { timeAgo } from "../lib/constants";
import { RoleBadge } from "./Badges";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { toast } from "sonner";

const ALL = ["super_admin", "admin", "lawyer", "requester"];
const STAFF = ["super_admin", "admin", "lawyer"];

const NAV = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, roles: ALL, testid: "nav-dashboard", end: true },
  { to: "/cases", labelKey: "nav.cases", icon: Briefcase, roles: ALL, testid: "nav-cases" },
  { to: "/cases/new", labelKey: "nav.newCase", icon: FilePlus2, roles: ALL, testid: "nav-new-case" },
  { to: "/kpi", labelKey: "nav.kpi", icon: BarChart3, roles: STAFF, testid: "nav-kpi" },
  { to: "/logs", labelKey: "nav.logs", icon: ScrollText, roles: STAFF, testid: "nav-logs" },
  { to: "/users", labelKey: "nav.users", icon: UsersIcon, roles: ["super_admin", "admin"], testid: "nav-users" },
];

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const { t, rtl } = useLanguage();
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 pb-6 pt-6">
        <div className="rounded-lg bg-white p-1.5 shadow-sm">
          <img src="/assets/delta-logo.png" alt="Delta Medical Laboratories" className="h-9 w-auto object-contain" />
        </div>
        <div>
          <p className="font-heading text-sm font-bold leading-tight text-white">Delta Legal Ops</p>
          <p className="text-[11px] leading-tight text-white/60">مختبرات دلتا الطبية</p>
        </div>
      </div>
      <div className="mx-5 mb-4 h-px bg-white/10" />
      <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto px-3" data-testid="sidebar-nav">
        {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${rtl ? "border-r-4" : "border-l-4"} ${
                isActive
                  ? "border-delta-lime bg-white/10 text-white"
                  : "border-transparent text-white/70 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <n.icon className="h-4 w-4 shrink-0" />
            {t(n.labelKey)}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 text-[11px] text-white/40">
        {t("nav.footer1")}
        <br />
        {t("nav.footer2")}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, rtl, toggle } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const unread = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const loadNotifs = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setNotifications(r.data);
    } catch {
      /* keep stale */
    }
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const openNotification = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch { /* ignore */ }
    setNotifOpen(false);
    if (n.case_id) navigate(`/cases/${n.case_id}`);
    loadNotifs();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      loadNotifs();
    } catch (e) {
      toast.error(apiError(e, t("header.notifFail")));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <aside className={`fixed inset-y-0 ${rtl ? "right-0" : "left-0"} z-30 hidden w-64 bg-delta-navy md:block`} data-testid="sidebar">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className={`absolute inset-y-0 ${rtl ? "right-0" : "left-0"} w-64 bg-delta-navy`}>
            <button
              data-testid="sidebar-close-button"
              onClick={() => setMobileOpen(false)}
              className={`absolute top-3 ${rtl ? "left-3" : "right-3"} rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white`}
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className={rtl ? "md:pr-64" : "md:pl-64"}>
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-white/85 px-4 backdrop-blur-md sm:px-6" data-testid="top-header">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden items-center gap-2 md:flex">
            <span className="font-heading text-sm font-semibold text-delta-navy">{t("nav.portal")}</span>
          </div>
          <div className={`flex items-center gap-2 sm:gap-4 ${rtl ? "mr-auto" : "ml-auto"}`}>
            <button
              data-testid="language-toggle-button"
              onClick={toggle}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-delta-limeBorder hover:bg-delta-limeLight hover:text-delta-navy"
            >
              <Globe className="h-3.5 w-3.5" />
              {rtl ? "English" : "العربية"}
            </button>
            <button
              data-testid="notification-bell-button"
              onClick={() => setNotifOpen(true)}
              className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-delta-limeLight hover:text-delta-navy"
            >
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-delta-lime px-1 text-[10px] font-bold text-white">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-delta-navy text-xs font-bold text-white" data-testid="user-avatar">
                {initials}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold leading-tight text-slate-800" data-testid="header-user-name">{user.name}</p>
                <RoleBadge role={user.role} testid="header-user-role" />
              </div>
              <Button
                variant="ghost"
                size="icon"
                data-testid="logout-button"
                onClick={handleLogout}
                className="text-slate-500 hover:bg-red-50 hover:text-red-600"
                title={t("header.signOut")}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md" data-testid="notifications-panel" aria-describedby={undefined}>
          <SheetHeader className="flex-row items-center justify-between space-y-0">
            <SheetTitle className="font-heading">{t("header.notifications")}</SheetTitle>
            {unread > 0 && (
              <Button variant="ghost" size="sm" data-testid="mark-all-read-button" onClick={markAllRead} className="text-delta-navy">
                <CheckCheck className="me-1 h-4 w-4" /> {t("header.markAllRead")}
              </Button>
            )}
          </SheetHeader>
          <div className="mt-4 space-y-2 overflow-y-auto pb-8" style={{ maxHeight: "calc(100vh - 120px)" }}>
            {notifications.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-500" data-testid="notifications-empty">{t("header.noNotifications")}</p>
            )}
            {notifications.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => openNotification(n)}
                className={`w-full rounded-lg border p-3 text-start transition-colors hover:border-delta-limeBorder hover:bg-delta-limeLight/50 ${
                  n.read ? "border-slate-200 bg-white" : "border-delta-limeBorder bg-delta-limeLight/30"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm ${n.read ? "font-medium text-slate-700" : "font-semibold text-slate-900"}`}>{n.title}</p>
                  {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-delta-lime" />}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{n.message}</p>
                <p className="mt-1 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
